"use client";

import { useQuery } from "@tanstack/react-query";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import { SpecialRow } from "@/data/specials";
import { SpecialWithTagInfo } from "@punchup/data/models";

import { Comedian } from "@/data/comedians";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { Database } from "@punchup/data/supabase.types";
import { getFollowedComedians } from "@/data/follows";
import { getUserCanAccessContent, Product } from "@punchup/data/legacy";
import { getAllProductsForSpecials } from "@/data/v2/client/public/products";
import { getUserPaidSpecials } from "@/data/v2/client/user/specials";
import { getSpecials } from "@/data/v2/client/public/specials";
import { getUserPaidSubscriptionTiers } from "@punchup/data/client/user/paid-subscriptions";

export type SpecialWithUnlockedAndCollection = SpecialWithTagInfo & {
  unlocked: boolean;
  products?: Product[];
};

interface UseUnlockedSpecialsForUserOptions {
  comedian: Comedian;
  isLoggedIn: boolean;
}

async function addSpecialUnlockedStatusForUser<T extends SpecialRow>(
  supabase: ReturnType<typeof createClientComponentClient<Database>>,
  specials: T[],
  loggedIn: boolean
): Promise<(T & { unlocked: boolean })[]> {
  let purchasedSpecialIds: string[] = [];
  let tiersByComedianId: Record<string, number> = {};
  let userFollowedComedians: { comedian_id: string }[] = [];

  if (loggedIn) {
    const [
      purchasedSpecialIdsResult,
      tiersByComedianIdResult,
      userFollowedComediansResult,
    ] = await Promise.all([
      getUserPaidSpecials().catch(() => []),
      getUserPaidSubscriptionTiers().catch(() => ({})),
      getFollowedComedians({ supabase }).catch(() => []),
    ]);

    purchasedSpecialIds = purchasedSpecialIdsResult.map(
      (special) => special.id
    );
    tiersByComedianId = tiersByComedianIdResult;
    userFollowedComedians = userFollowedComediansResult;
  }

  const userFollowedComedianIds = userFollowedComedians.map(
    (object) => object.comedian_id
  );

  const specialsWithUnlocked = specials.map((special) => {
    const userPurchasedSpecial = purchasedSpecialIds.includes(special.id);
    const userMaxSubscriptionTier = tiersByComedianId[special.comedian_id] ?? 0;
    const isUserSubscribed = userMaxSubscriptionTier > 0;
    const userFollowsComedian = userFollowedComedianIds.includes(
      special.comedian_id
    );

    const unlocked = getUserCanAccessContent({
      special,
      userFollowsComedian,
      userPurchasedSpecial,
      isUserSubscribed,
      userMaxSubscriptionTier,
    });
    return { ...special, unlocked };
  });

  return specialsWithUnlocked;
}

/**
 * Hook to fetch a comedian's specials with unlock status for the current user.
 * Determines which specials the user can access based on purchases, subscriptions, and follows.
 *
 * @param comedian - The comedian whose specials to fetch
 * @param isLoggedIn - Whether the user is currently logged in
 */
export function useUnlockedSpecialsForUser({
  comedian,
  isLoggedIn,
}: UseUnlockedSpecialsForUserOptions) {
  const queryFn = async (): Promise<SpecialWithUnlockedAndCollection[]> => {
    const supabase = createClientComponentClient<Database>();

    try {
      const allSpecials = await getSpecials({
        comedianId: comedian.id,
        limit: 500,
        tags: "include",
      });
      const specialsWithUnlocked = await addSpecialUnlockedStatusForUser(
        supabase,
        allSpecials,
        isLoggedIn
      );

      const lockedSpecials = specialsWithUnlocked.filter((s) => !s.unlocked);

      const lockedSpecialIds = lockedSpecials.map((s) => s.id);

      // Batch product fetches to avoid 431 (URL too large) when many specials are locked.
      // With ~14KB of cookies in dev, each request needs a URL under ~1200 chars.
      // 30 UUIDs × 37 chars = ~1110 chars, which keeps total headers safely under 16KB.
      const PRODUCTS_BATCH_SIZE = 30;
      let productsBySpecialId: Record<string, Product[]> = {};
      if (lockedSpecialIds.length > 0) {
        const batches: string[][] = [];
        for (let i = 0; i < lockedSpecialIds.length; i += PRODUCTS_BATCH_SIZE) {
          batches.push(lockedSpecialIds.slice(i, i + PRODUCTS_BATCH_SIZE));
        }
        const results = await Promise.allSettled(
          batches.map((batch) => getAllProductsForSpecials(batch))
        );
        for (const result of results) {
          if (result.status === "fulfilled") {
            Object.assign(productsBySpecialId, result.value);
          }
        }
      }

      return specialsWithUnlocked.map((special) => ({
        ...special,
        products: productsBySpecialId[special.id],
      }));
    } catch (error) {
      logException(error as Error, {
        extra: {
          data: `Error fetching unlocked specials inside useUnlockedSpecialsForUser hook`,
        },
        tags: {
          supabase: true,
          feature: SentryFeatureTags.Specials,
          comedianId: comedian.id,
        },
      });
      throw error;
    }
  };

  const {
    data: specials,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["unlocked-specials", comedian.id, isLoggedIn],
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return { specials: specials ?? [], isLoading, error, refetch };
}
